import { Fragment, useState } from 'react';

import { CellMenu, IMenuItem } from '@client/new/components/TableShared/Cell';
import { Tooltip } from '@design-system/components/tooltip/tooltip';
import { Typography } from '@design-system/components/typography/typography';
import { textColorsVars } from '@design-system/vars.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartSimple,
  faGripDotsVertical,
  faShareNodes,
} from '@fortawesome/pro-regular-svg-icons';
import { faEyeSlash, faImage } from '@fortawesome/pro-solid-svg-icons';
import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';

import { ProgramMetrics, ProgramStateKind } from '../../model/programs.types';
import {
  ChallengeIndicator,
  ProgramCardRow,
  ProgramsVM,
} from '../../view-model/programs.viewModel.types';

import { OffersCell } from './OffersCell';
import { StatusTag } from './StatusTag';
import * as styles from './ProgramCard.css';

type ProgramCardProps = {
  row: ProgramCardRow;
  dispatch: ProgramsVM['dispatch'];
  isDnDEnabled: boolean;
  dndProps?: DraggableProvidedDragHandleProps | null;
};

const isDimmedState = (state: ProgramStateKind): boolean =>
  state === 'draft' || state === 'archived';

const toMetricValue = (value: number | null, withDecimal?: boolean): string => {
  if (value === null || value === 0) return '-';
  if (withDecimal) return value.toFixed(1);
  return String(value);
};

const toCellMenuItems = (
  items: ProgramCardRow['menuItems'],
): Array<IMenuItem> =>
  items.map((item) => ({
    text: item.label,
    onClick: item.onClick,
    disabled: item.disabled,
    secondaryText: item.disabledReason,
    color: item.destructive ? 'error' : undefined,
  }));

const stopPropagation = (event: React.MouseEvent): void => {
  event.stopPropagation();
};

const HiddenImageBadge = () => (
  <div className={styles.hiddenOverlay}>
    <div className={styles.hiddenOverlayBadge}>
      <FontAwesomeIcon icon={faEyeSlash} className={styles.hiddenOverlayIcon} />
    </div>
  </div>
);

const ChallengeFlag = ({ indicator }: { indicator: ChallengeIndicator }) => (
  <Tooltip
    arrow
    variant="dark"
    title={indicator.tooltip}
    placement="top"
    tooltipProps={{ maxWidth: 180 }}
  >
    <span
      className={styles.challengeFlag}
      style={{ color: textColorsVars[indicator.color] }}
      onClick={stopPropagation}
      data-testid="program-card-challenge-flag"
    >
      <FontAwesomeIcon icon={indicator.icon} />
    </span>
  </Tooltip>
);

type CardImageProps = {
  row: ProgramCardRow;
};

const CardImage = ({ row }: CardImageProps) => {
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = row.mediaCover && !imageFailed;

  const inner = (
    <div className={styles.imageWrapper}>
      <div
        className={styles.imageContainer({ dimmed: row.notVisibleToConsumers })}
      >
        {showImage && row.mediaCover ? (
          <img
            src={row.mediaCover.cdn01 + row.mediaCover.image}
            alt={row.name}
            className={styles.image}
            onError={() => setImageFailed(true)}
            data-testid={`program-card-image-${row.id}`}
          />
        ) : (
          <div
            className={styles.placeholder}
            data-testid={`program-card-placeholder-${row.id}`}
          >
            <FontAwesomeIcon
              icon={faImage}
              className={styles.placeholderIcon}
            />
          </div>
        )}
      </div>
      {row.notVisibleToConsumers ? <HiddenImageBadge /> : null}
      {row.challengeIndicator ? (
        <ChallengeFlag indicator={row.challengeIndicator} />
      ) : null}
    </div>
  );

  if (row.tooltipText) {
    return (
      <Tooltip
        arrow
        variant="dark"
        title={row.tooltipText}
        tooltipProps={{ maxWidth: 220 }}
      >
        {inner}
      </Tooltip>
    );
  }

  return inner;
};

type StatsTooltipProps = {
  metrics: ProgramMetrics;
  commentsCount: number;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

const StatsTooltip = ({
  metrics,
  commentsCount,
  isOpen,
  onOpenChange,
}: StatsTooltipProps) => {
  const rows: Array<{ label: string; value: string }> = [
    { label: 'Rating', value: toMetricValue(metrics.rating, true) },
    { label: 'Begins', value: toMetricValue(metrics.begins) },
    { label: 'Completion', value: toMetricValue(metrics.completions) },
    { label: 'Comments', value: toMetricValue(commentsCount) },
  ];

  const content = (
    <div className={styles.tooltipContent}>
      {rows.map((r) => (
        <Fragment key={r.label}>
          <span className={styles.tooltipLabel}>{r.label}</span>
          <span className={styles.tooltipValue}>{r.value}</span>
        </Fragment>
      ))}
    </div>
  );

  return (
    <div className={styles.statsTooltipCell} onClick={stopPropagation}>
      <Tooltip
        arrow
        variant="dark"
        title={content}
        placement="top"
        tooltipProps={{ maxWidth: 240 }}
        open={isOpen}
        onOpen={() => onOpenChange(true)}
        onClose={() => onOpenChange(false)}
      >
        <button
          type="button"
          aria-label="Show all stats"
          className={styles.statsTooltipButton}
          data-testid="program-card-stats-summary"
        >
          <FontAwesomeIcon icon={faChartSimple} />
        </button>
      </Tooltip>
    </div>
  );
};

export const ProgramCard = ({
  row,
  dispatch,
  isDnDEnabled,
  dndProps,
}: ProgramCardProps) => {
  const [isStatsTooltipOpen, setIsStatsTooltipOpen] = useState(false);

  const handleRowClick = () => {
    if (row.isReorderPending) return;
    dispatch('EditProgram', row.id);
  };

  return (
    <div
      id={String(row.id)}
      data-testid={`program-card-${row.id}`}
      className={styles.wrapper({
        pending: row.isReorderPending,
        dnd: isDnDEnabled,
      })}
      onClick={handleRowClick}
      {...dndProps}
    >
      {isDnDEnabled ? (
        <div
          className={styles.dragGrip}
          aria-hidden="true"
          data-testid={`program-card-grip-${row.id}`}
        >
          <FontAwesomeIcon icon={faGripDotsVertical} />
        </div>
      ) : null}
      <CardImage row={row} />
      <div className={styles.textWrapper}>
        <Typography
          variant="body"
          size="large"
          fontWeight="normal"
          className={styles.title({ dimmed: isDimmedState(row.state) })}
        >
          {row.name}
        </Typography>
        <Typography
          variant="body"
          size="small"
          fontWeight="normal"
          className={styles.subtitle({ dimmed: isDimmedState(row.state) })}
        >
          {row.subInfo}
        </Typography>
      </div>

      <span onClick={stopPropagation}>
        <StatusTag
          state={row.state}
          readiness={row.readiness}
          onReadinessClick={
            row.readiness
              ? (anchor) =>
                  dispatch('ShowReadinessDetails', {
                    programId: row.id,
                    anchor,
                  })
              : undefined
          }
        />
      </span>

      <div className={styles.offersCellWrapper} onClick={stopPropagation}>
        <OffersCell
          offerState={row.offerState}
          addToOfferActions={row.addToOfferActions}
          addToOfferDirectAction={row.addToOfferDirectAction}
          removeFromSubscriptionAction={row.removeFromSubscriptionAction}
        />
      </div>

      <Typography
        variant="body"
        size="small"
        fontWeight="normal"
        className={styles.statCell({ hidden: isStatsTooltipOpen })}
      >
        {toMetricValue(row.metrics.rating, true)}
      </Typography>

      <Typography
        variant="body"
        size="small"
        fontWeight="normal"
        className={styles.statCellBegins({ hidden: isStatsTooltipOpen })}
      >
        {toMetricValue(row.metrics.begins)}
      </Typography>

      <Typography
        variant="body"
        size="small"
        fontWeight="normal"
        className={styles.statCellCompletion({ hidden: isStatsTooltipOpen })}
      >
        {toMetricValue(row.metrics.completions)}
      </Typography>

      <Typography
        variant="body"
        size="small"
        fontWeight="normal"
        className={styles.statCellComments({ hidden: isStatsTooltipOpen })}
      >
        {toMetricValue(row.commentsCount)}
      </Typography>

      <StatsTooltip
        metrics={row.metrics}
        commentsCount={row.commentsCount}
        isOpen={isStatsTooltipOpen}
        onOpenChange={setIsStatsTooltipOpen}
      />

      <div className={styles.trailingActions}>
        {row.state === 'published' ? (
          <button
            type="button"
            aria-label="Share program links"
            className={styles.iconButton}
            onClick={(e) => {
              e.stopPropagation();
              dispatch('OpenLinks', row.id);
            }}
          >
            <FontAwesomeIcon icon={faShareNodes} />
          </button>
        ) : null}
      </div>

      <div onClick={stopPropagation}>
        <CellMenu items={toCellMenuItems(row.menuItems)} />
      </div>
    </div>
  );
};
